import { FC, useMemo } from 'react';
import {
    IressCol,
    IressForm,
    IressFormField,
    IressFormFieldset,
    IressInput,
    IressInputCurrency,
    IressRadio,
    IressRadioGroup,
    IressRow,
    IressSelect,
    IressStack,
    IressText,
} from '@iress-oss/ids-components';
import {
    CURRENCY_CODE,
    ErrorMessage,
    EscalationType,
    FrequencyType,
    LOCALE,
    OngoingContributionValues,
} from '@xplan-pension-switching/common';
import { formatTwoDecimalPlaces, twoDecimalPlaceRegex } from '../../../helpers/Numbers/TwoDecimalPlaces';
import { escalationTypeOptions, anniversaryTypeOptions } from './OngoingContributionsSelectOptions';
import { Section } from '../../Section/Section';

export const OngoingContributions: FC<{
    ongoingContributions: OngoingContributionValues;
}> = ({ ongoingContributions }) => {
    // TODO: Temporary defaulting as OngoingContributions is not currently returned from BFF. Remove when NEP-197 is complete
    const defaultOngoingContributions: OngoingContributionValues = {
        frequency: FrequencyType.Monthly,
        grossPremium: 0,
        escalationType: undefined,
        escalationPercentage: undefined,
        increasingEvery: undefined,
    };
    const tempOngoingContributions = ongoingContributions ?? defaultOngoingContributions;

    const { watch } = IressForm.useFormContext();
    const frequencyFormValue = watch('ongoingContributions.frequency', tempOngoingContributions.frequency);
    const grossPremiumFormValue = watch('ongoingContributions.grossPremium', tempOngoingContributions.grossPremium);
    const escalationTypeFormValue = watch(
        'ongoingContributions.escalationType',
        tempOngoingContributions.escalationType
    );

    const showEscalationType = useMemo(() => Number(grossPremiumFormValue) > 0, [grossPremiumFormValue]);
    const showEscalationPercentage = useMemo(
        () => escalationTypeFormValue === EscalationType.Other,
        [escalationTypeFormValue]
    );
    const showIncreasingEvery = useMemo(
        () =>
            !(
                Number(grossPremiumFormValue) === 0 ||
                (frequencyFormValue === FrequencyType.Monthly &&
                    Number(grossPremiumFormValue) > 0 &&
                    (escalationTypeFormValue === EscalationType.ZeroPercent || !escalationTypeFormValue))
            ),
        [grossPremiumFormValue, escalationTypeFormValue, frequencyFormValue]
    );

    return (
        <Section heading="Ongoing contributions" headingLevel="h3">
            <IressStack gutter="lg">
                <IressFormFieldset
                    label="Frequency"
                    name="ongoingContributions.frequency"
                    defaultValue={tempOngoingContributions.frequency}
                    rules={{ required: ErrorMessage.SelectAnOption }}
                    render={controlledProps => (
                        <IressRadioGroup layout="inline" {...controlledProps}>
                            <IressRadio value={FrequencyType.Annually}>Annually</IressRadio>
                            <IressRadio value={FrequencyType.Monthly}>Monthly</IressRadio>
                        </IressRadioGroup>
                    )}
                />
                <IressRow gutter="lg" verticalAlign="bottom">
                    <IressCol span="3">
                        <IressFormField
                            label="Gross premium"
                            name="ongoingContributions.grossPremium"
                            defaultValue={tempOngoingContributions.grossPremium}
                            rules={{
                                required: ErrorMessage.EnterAValue,
                                min: { value: 0.01, message: ErrorMessage.MinGreaterThanZero },
                                pattern: twoDecimalPlaceRegex,
                            }}
                            render={controlledProps => (
                                <IressInputCurrency
                                    type="number"
                                    currencyCode={CURRENCY_CODE}
                                    locale={LOCALE}
                                    {...controlledProps}
                                />
                            )}
                        />
                    </IressCol>
                    {showEscalationType && (
                        <IressCol span="3">
                            <IressFormField
                                label="Escalation"
                                name="ongoingContributions.escalationType"
                                defaultValue={tempOngoingContributions.escalationType}
                                rules={{
                                    required: ErrorMessage.SelectAnOption,
                                }}
                                render={controlledProps => (
                                    <IressSelect placeholder="Select an option" {...controlledProps}>
                                        {escalationTypeOptions.map(option => (
                                            <option key={String(option.value)} value={String(option.value)}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </IressSelect>
                                )}
                            />
                        </IressCol>
                    )}
                    {showEscalationPercentage && (
                        <IressCol span="3">
                            <IressFormField
                                label="Percentage"
                                name="ongoingContributions.escalationPercentage"
                                defaultValue={tempOngoingContributions.escalationPercentage ?? 0}
                                rules={{
                                    required: ErrorMessage.EnterAValue,
                                    min: { value: 0.01, message: ErrorMessage.MinGreaterThanZero },
                                    max: { value: 20, message: 'Enter a value less than or equal to 20' },
                                    pattern: twoDecimalPlaceRegex,
                                }}
                                render={controlledProps => (
                                    <IressInput
                                        type="number"
                                        formatter={value => formatTwoDecimalPlaces(value)}
                                        append={<IressText>%</IressText>}
                                        {...controlledProps}
                                    />
                                )}
                            />
                        </IressCol>
                    )}
                    {showIncreasingEvery && (
                        <IressCol span="3">
                            <IressFormField
                                label="Increasing every / policy anniversary"
                                name="ongoingContributions.increasingEvery"
                                defaultValue={tempOngoingContributions.increasingEvery}
                                rules={{
                                    required: ErrorMessage.SelectAnOption,
                                }}
                                render={controlledProps => (
                                    <IressSelect placeholder=" " {...controlledProps}>
                                        {anniversaryTypeOptions.map(option => (
                                            <option key={String(option.value)} value={String(option.value)}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </IressSelect>
                                )}
                            />
                        </IressCol>
                    )}
                </IressRow>
            </IressStack>
        </Section>
    );
};
